import { X, Check, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { CategoriesFilter } from './CategoriesFilter';
import { BaseActionSheet } from './BaseActionSheet';
import { FilterState } from '../App';
import { FilterSwitch } from './FilterSwitch';
import { FilterActionButton } from './FilterActionButton';
import { IUser } from '@/types/user';
import { UserService } from '@/services/store/user';
import { ISort } from '@/services/store/interfaces/offers.interfaces';
import {
  IGetCepResponse,
  IGetUserLocationResponse,
  IUpdateUserLocationRequest,
} from '@/services/store/interfaces/user.interface';
import { ButtonLoading, ButtonLoadingBlue } from './Loading';

interface FilterBottomSheetProps {
  isOpen: boolean;
  type: 'location' | 'sort' | 'filters' | 'categories' | null;
  onClose: () => void;
  sortOption?: string;
  onSortChange?: (option: string) => void;
  filters?: FilterState;
  onFiltersChange?: (filters: FilterState) => void;
  selectedCategories?: string[];
  onCategoriesChange?: (categories: string[]) => void;
  onOpenPrototypeSettings?: () => void;
  userInfo: IUser;
  sortOptions: ISort[];
  handleApplySort: () => Promise<void>;
  authToken: string;
  handleClearSort: () => Promise<void>;
  isLoadingSort: boolean;
}

export function FilterBottomSheet({
  isOpen,
  type,
  onClose,
  sortOption = 'recommended',
  onSortChange,
  filters,
  onFiltersChange,
  selectedCategories = [],
  onCategoriesChange,
  onOpenPrototypeSettings,
  userInfo,
  sortOptions,
  handleApplySort,
  authToken,
  handleClearSort,
  isLoadingSort,
}: FilterBottomSheetProps) {
  if (!type) return null;

  // For filters, we'll handle the footer separately
  if (type === 'filters') {
    return (
      <AdvancedFiltersSheet
        isOpen={isOpen}
        onClose={onClose}
        filters={filters}
        onFiltersChange={onFiltersChange}
      />
    );
  }

  return (
    <BaseActionSheet
      isOpen={isOpen}
      onClose={onClose}
      title={getSheetTitle(type)}
    >
      {type === 'location' && (
        <LocationFilter
          onClose={onClose}
          userInfo={userInfo}
          authToken={authToken}
        />
      )}
      {type === 'sort' && (
        <SortFilter
          onClose={onClose}
          selectedSort={sortOption}
          onSortChange={onSortChange}
          sortOptionsList={sortOptions}
          handleApplySort={handleApplySort}
          isLoadingSort={isLoadingSort}
          handleClearSort={handleClearSort}
          sortOption={sortOption}
        />
      )}
      {type === 'categories' && (
        <CategoriesFilter
          onClose={onClose}
          selectedCategories={selectedCategories}
          onCategoriesChange={onCategoriesChange}
        />
      )}
    </BaseActionSheet>
  );
}

function getSheetTitle(type: string | null): string {
  switch (type) {
    case 'location':
      return 'Localização';
    case 'sort':
      return 'Ordenar pedidos';
    case 'filters':
      return 'Ocultar';
    case 'categories':
      return 'Categorias';
    default:
      return '';
  }
}

// LOCALIZAÇÃO AQUI
function LocationFilter({
  onClose,
  userInfo,
  authToken,
}: {
  onClose: () => void;
  userInfo: IUser;
  authToken: string;
}) {
  const userCep = userInfo?.profile?.address?.zip_code || '';
  const location = `${userInfo?.profile?.address?.street} • ${userInfo?.profile?.address?.neighborhood}, ${userInfo?.profile?.address?.uf} `;

  const [isLoadingLocation, setIsLoadingLocation] = useState<boolean>(false);

  const [userLocation, setUserLocation] = useState(location);
  const [cepAddress, setCepAddress] = useState<IGetCepResponse>();
  const [userLocationFields, setUserLocationFields] =
    useState<IGetUserLocationResponse>();
  const [radius, setRadius] = useState(0);

  const handleGetUserLocation = useCallback(async () => {
    await UserService.getUserLocation(authToken).then((res) => {
      setRadius(res.radius);
      setUserLocationFields(res);
    });
  }, [authToken]);

  const formatCep = (cep: string) => {
    return cep.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  const handleGetCep = async (val: string) => {
    const res = await UserService.getCep(val);
    setCepAddress(res);

    const newLocation = `${res.street} • ${res.neighborhood}, ${res.uf} `;
    setUserLocation(newLocation || '');
  };

  const [zipCode, setZipCode] = useState(formatCep(userCep));

  useEffect(() => {
    void handleGetUserLocation();
  }, [authToken]);

  const street =
    userLocationFields?._embedded?.address_fields.find(
      (field) => field.name === 'address'
    )?.current_value || '';
  const number =
    userLocationFields?._embedded?.address_fields.find(
      (field) => field.name === 'number'
    )?.current_value || '';

  const details =
    userLocationFields?._embedded?.address_fields.find(
      (field) => field.name === 'address_detail'
    )?.current_value || '';

  const userAddress = cepAddress ? cepAddress.street : street;
  const userNumber = cepAddress ? '' : number;
  const userDetails = cepAddress ? '' : details;

  const updateUserObj: IUpdateUserLocationRequest = {
    userId: userInfo.id,
    authToken,
    radius,
    address_fields: {
      zip_code: zipCode,
      address: userAddress,
      address_detail: userDetails,
      number: userNumber,
    },
  };

  const handleUpdateUserLocation = async () => {
    setIsLoadingLocation(true);
    await UserService.updateUserLocation(updateUserObj)
      .then((res) => {
        setIsLoadingLocation(false);
        console.log(res);
      })
      .catch((error) => {
        setIsLoadingLocation(false);
        console.error(error);
      });
  };

  return (
    <div
      className="space-y-6"
      style={{
        paddingLeft: '20px',
        paddingRight: '20px',
      }}
    >
      <div>
        <label
          style={{
            display: 'block',
            fontSize: '1rem', // text-sm (14px)
            lineHeight: '1.25rem',
            color: '#4b5563', // gray-600
            marginBottom: '0.5rem', // mb-2 (8px)
          }}
        >
          CEP
        </label>
        <input
          type="text"
          value={zipCode}
          onChange={(e) => {
            setZipCode(formatCep(e.target.value));

            if (/^\d{5}-?\d{3}$/.test(e.target.value)) {
              handleGetCep(e.target.value);
            }
          }}
          placeholder="00000-000"
          style={{
            width: '100%',
            paddingLeft: '16px',
            paddingRight: '16px',
            paddingTop: '12px',
            paddingBottom: '12px',
            border: '1px solid #d1d5db', // gray-300
            borderRadius: '12px', // rounded-xl
            outline: 'none',
          }}
        />
        {/* Address preview */}
        <div
          className="mt-3 px-3 py-2.5 flex items-start gap-2"
          style={{
            marginTop: '1rem',
            padding: '0.875rem 0.7rem',
            backgroundColor: 'var(--muted)',
            borderRadius: 'var(--radius-button)',
            border: '1px solid var(--border)',
            marginBottom: '1.5rem',
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              color: 'var(--muted-foreground)',
              marginTop: '2px',
              flexShrink: 0,
            }}
          >
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          <span
            style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--muted-foreground)',
              lineHeight: '1.4',
            }}
          >
            {userLocation}
          </span>
        </div>
      </div>

      <div>
        <label
          style={{
            display: 'block',
            fontSize: '1rem', // text-sm
            lineHeight: '1.25rem',
            color: '#4b5563', // text-gray-600
            marginBottom: '0.5rem', // mb-2
          }}
        >
          Raio: {radius} km
        </label>
        <input
          type="range"
          min="1"
          max="100"
          value={radius}
          onChange={(e) => setRadius(Number(e.target.value))}
          style={{
            width: '100%',
            height: '0.5rem', // h-2 (8px)
            backgroundColor: '#e5e7eb', // bg-gray-200
            borderRadius: '0.5rem', // rounded-lg (8px)
            appearance: 'none',
            WebkitAppearance: 'none',
            cursor: 'pointer',
            accentColor: '#2563eb', // accent-blue-600
          }}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '1rem', // text-xs (12px)
            lineHeight: '1rem',
            color: '#6b7280', // text-gray-500
            marginTop: '0.3rem', // mt-1 (4px)
            marginBottom: '2rem',
          }}
        >
          <span>1 km</span>
          <span>100 km</span>
        </div>
      </div>

      <FilterActionButton
        onClick={handleUpdateUserLocation}
        loading={isLoadingLocation}
      >
        Aplicar localização
      </FilterActionButton>
    </div>
  );
}

function SortFilter({
  onClose,
  selectedSort,
  onSortChange,
  sortOptionsList,
  handleApplySort,
  handleClearSort,
  sortOption,
  isLoadingSort,
}: {
  onClose: () => void;
  selectedSort: string;
  onSortChange?: (option: string) => void;
  sortOptionsList: ISort[];
  handleApplySort: () => Promise<void>;
  handleClearSort: () => Promise<void>;
  sortOption: string;
  isLoadingSort: boolean;
}) {
  const sortOptions = sortOptionsList.map((option) => {
    return {
      key: option.type,
      label: option.label,
      description: option.label,
    };
  });

  return (
    <div
      className="space-y-2"
      style={{
        paddingLeft: '20px',
        paddingRight: '20px',
      }}
    >
      {sortOptions.map((option) => (
        <motion.button
          key={option.key}
          onClick={() => onSortChange && onSortChange(option.key)}
          className="w-full transition-colors text-left flex items-center gap-3"
          style={{
            paddingLeft: '16px',
            paddingRight: '16px',
            paddingTop: '14px',
            paddingBottom: '14px',
            borderRadius: 'var(--radius-card)',
            backgroundColor:
              selectedSort === option.key ? 'var(--muted)' : 'transparent',
            borderWidth: 0,
          }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex-1 min-w-0">
            <h4
              className="text-foreground"
              style={{
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-weight-semibold)',
                lineHeight: '1.4',
                marginBottom: '2px',
              }}
            >
              {option.label}
            </h4>
            {/*<p
              className="text-muted-foreground"
              style={{
                fontSize: 'var(--text-sm)',
                lineHeight: '1.4',
              }}
            >
              {option.description}
            </p>*/}
          </div>
          {/* Checkmark - right aligned with consistent spacing */}
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{
              width: '20px',
              height: '20px',
            }}
          >
            {selectedSort === option.key && (
              <Check
                className="w-5 h-5"
                style={{ color: 'var(--kojo-blue)' }}
              />
            )}
          </div>
        </motion.button>
      ))}

      {sortOption !== 'recommended' && !isLoadingSort && (
        <div
          style={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <motion.button
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            onClick={handleClearSort}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '0',
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--kojo-blue)',
              textAlign: 'center',
              paddingTop: '8px',
              paddingBottom: '8px',
            }}
            disabled={isLoadingSort}
          >
            Limpar Ordenação
          </motion.button>
        </div>
      )}

      <div style={{ marginTop: '20px' }}>
        <FilterActionButton
          onClick={handleApplySort}
          loading={isLoadingSort && sortOption !== 'recommended'}
        >
          Aplicar ordenação
        </FilterActionButton>
      </div>
    </div>
  );
}

function AdvancedFiltersSheet({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
}: {
  isOpen: boolean;
  onClose: () => void;
  filters?: FilterState;
  onFiltersChange?: (filters: FilterState) => void;
}) {
  // NEW DEFAULT STATE: ALL SWITCHES ON (all filters included by default)
  const defaultFilters: FilterState = {
    urgente: true,
    naoUrgente: true,
    presencial: true,
    remoto: true,
    ninguemLiberou: true,
    outrosLiberaram: true,
    naoAvaliados: true,
    gostei: true,
    naoGostei: true,
  };

  // Local state initialized from props with correct default
  const [localFilters, setLocalFilters] = useState<FilterState>(
    filters || defaultFilters
  );

  // Helper to check if "Mostrar tudo" button should be visible
  const hasAnyFilterOff = () => {
    return Object.values(localFilters).some((value) => value === false);
  };

  // Group A - Por urgência (can have both ON, but NOT both OFF)
  const toggleUrgente = () => {
    setLocalFilters((prev) => {
      const newUrgente = !prev.urgente;
      // If turning OFF urgente, and naoUrgente is already OFF, turn ON naoUrgente
      if (!newUrgente && !prev.naoUrgente) {
        return {
          ...prev,
          urgente: false,
          naoUrgente: true,
        };
      }
      return {
        ...prev,
        urgente: newUrgente,
      };
    });
  };

  const toggleNaoUrgente = () => {
    setLocalFilters((prev) => {
      const newNaoUrgente = !prev.naoUrgente;
      // If turning OFF naoUrgente, and urgente is already OFF, turn ON urgente
      if (!newNaoUrgente && !prev.urgente) {
        return {
          ...prev,
          naoUrgente: false,
          urgente: true,
        };
      }
      return {
        ...prev,
        naoUrgente: newNaoUrgente,
      };
    });
  };

  // Group B - Por concorrência (can have both ON, but NOT both OFF)
  const toggleNinguemLiberou = () => {
    setLocalFilters((prev) => {
      const newNinguemLiberou = !prev.ninguemLiberou;
      // If turning OFF, and outrosLiberaram is already OFF, turn ON outrosLiberaram
      if (!newNinguemLiberou && !prev.outrosLiberaram) {
        return {
          ...prev,
          ninguemLiberou: false,
          outrosLiberaram: true,
        };
      }
      return {
        ...prev,
        ninguemLiberou: newNinguemLiberou,
      };
    });
  };

  const toggleOutrosLiberaram = () => {
    setLocalFilters((prev) => {
      const newOutrosLiberaram = !prev.outrosLiberaram;
      // If turning OFF, and ninguemLiberou is already OFF, turn ON ninguemLiberou
      if (!newOutrosLiberaram && !prev.ninguemLiberou) {
        return {
          ...prev,
          outrosLiberaram: false,
          ninguemLiberou: true,
        };
      }
      return {
        ...prev,
        outrosLiberaram: newOutrosLiberaram,
      };
    });
  };

  // Group C - Por tipo de atendimento (can have both ON, but NOT both OFF)
  const togglePresencial = () => {
    setLocalFilters((prev) => {
      const newPresencial = !prev.presencial;
      // If turning OFF, and remoto is already OFF, turn ON remoto
      if (!newPresencial && !prev.remoto) {
        return {
          ...prev,
          presencial: false,
          remoto: true,
        };
      }
      return {
        ...prev,
        presencial: newPresencial,
      };
    });
  };

  const toggleRemoto = () => {
    setLocalFilters((prev) => {
      const newRemoto = !prev.remoto;
      // If turning OFF, and presencial is already OFF, turn ON presencial
      if (!newRemoto && !prev.presencial) {
        return {
          ...prev,
          remoto: false,
          presencial: true,
        };
      }
      return {
        ...prev,
        remoto: newRemoto,
      };
    });
  };

  // Group D - Por avaliação (can have all ON, but NOT all OFF - at least one ON)
  const toggleGostei = () => {
    setLocalFilters((prev) => {
      const newGostei = !prev.gostei;
      // If turning off, check if at least one other is on
      if (!newGostei && !prev.naoGostei && !prev.naoAvaliados) {
        // Turn on naoAvaliados as default
        return {
          ...prev,
          gostei: false,
          naoAvaliados: true,
        };
      }
      return {
        ...prev,
        gostei: newGostei,
      };
    });
  };

  const toggleNaoGostei = () => {
    setLocalFilters((prev) => {
      const newNaoGostei = !prev.naoGostei;
      // If turning off, check if at least one other is on
      if (!newNaoGostei && !prev.gostei && !prev.naoAvaliados) {
        // Turn on naoAvaliados as default
        return {
          ...prev,
          naoGostei: false,
          naoAvaliados: true,
        };
      }
      return {
        ...prev,
        naoGostei: newNaoGostei,
      };
    });
  };

  const toggleNaoAvaliados = () => {
    setLocalFilters((prev) => {
      const newNaoAvaliados = !prev.naoAvaliados;
      // If turning off, check if at least one other is on
      if (!newNaoAvaliados && !prev.gostei && !prev.naoGostei) {
        // Turn on gostei as default
        return {
          ...prev,
          naoAvaliados: false,
          gostei: true,
        };
      }
      return {
        ...prev,
        naoAvaliados: newNaoAvaliados,
      };
    });
  };

  const handleApply = () => {
    if (onFiltersChange) {
      onFiltersChange(localFilters);
    }
    onClose();
  };

  const handleShowAll = () => {
    setLocalFilters(defaultFilters);
  };

  return (
    <BaseActionSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Mostrar"
      footer={
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          {/* Show All Button - only visible when at least one switch is OFF */}
          {hasAnyFilterOff() && (
            <motion.button
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              onClick={handleShowAll}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '0',
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--kojo-blue)',
                textAlign: 'center',
                paddingTop: '8px',
                paddingBottom: '8px',
              }}
            >
              Mostrar tudo
            </motion.button>
          )}

          {/* Apply Button */}
          <FilterActionButton onClick={handleApply}>
            Aplicar filtros
          </FilterActionButton>
        </div>
      }
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0px',
          paddingBottom: '4px',
        }}
      >
        {/* GROUP A — POR URGÊNCIA */}
        <div
          style={{
            paddingTop: '24px',
            paddingBottom: '0px',
            paddingLeft: '20px',
            paddingRight: '20px',
          }}
        >
          <h4
            style={{
              fontSize: 'var(--text-xs)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--muted-foreground)',
              textTransform: 'uppercase',
              letterSpacing: '0.025em',
              marginBottom: '12px',
            }}
          >
            Urgência
          </h4>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0px',
              backgroundColor: 'var(--muted)',
              borderRadius: 'var(--radius-xl)',
              padding: '8px 20px',
            }}
          >
            <FilterSwitch
              enabled={localFilters.urgente}
              onToggle={toggleUrgente}
              label="Urgentes"
            />
            <FilterSwitch
              enabled={localFilters.naoUrgente}
              onToggle={toggleNaoUrgente}
              label="Não urgentes"
            />
          </div>
        </div>

        {/* Full-width divider between groups */}
        <div
          style={{
            height: '1px',
            backgroundColor: 'var(--border)',
            marginTop: '24px',
            marginBottom: '0px',
          }}
        />

        {/* GROUP B — POR CONCORRÊNCIA */}
        <div
          style={{
            paddingTop: '24px',
            paddingBottom: '0px',
            paddingLeft: '20px',
            paddingRight: '20px',
          }}
        >
          <h4
            style={{
              fontSize: 'var(--text-xs)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--muted-foreground)',
              textTransform: 'uppercase',
              letterSpacing: '0.025em',
              marginBottom: '12px',
            }}
          >
            Concorrência
          </h4>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0px',
              backgroundColor: 'var(--muted)',
              borderRadius: 'var(--radius-xl)',
              padding: '8px 20px',
            }}
          >
            <FilterSwitch
              enabled={localFilters.ninguemLiberou}
              onToggle={toggleNinguemLiberou}
              label="Ninguém liberou ainda"
            />
            <FilterSwitch
              enabled={localFilters.outrosLiberaram}
              onToggle={toggleOutrosLiberaram}
              label="Outros já liberaram"
            />
          </div>
        </div>

        {/* Full-width divider between groups */}
        <div
          style={{
            height: '1px',
            backgroundColor: 'var(--border)',
            marginTop: '24px',
            marginBottom: '0px',
          }}
        />

        {/* GROUP C — POR LOCAL */}
        <div
          style={{
            paddingTop: '24px',
            paddingBottom: '0px',
            paddingLeft: '20px',
            paddingRight: '20px',
          }}
        >
          <h4
            style={{
              fontSize: 'var(--text-xs)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--muted-foreground)',
              textTransform: 'uppercase',
              letterSpacing: '0.025em',
              marginBottom: '12px',
            }}
          >
            Local do serviço
          </h4>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0px',
              backgroundColor: 'var(--muted)',
              borderRadius: 'var(--radius-xl)',
              padding: '8px 20px',
            }}
          >
            <FilterSwitch
              enabled={localFilters.presencial}
              onToggle={togglePresencial}
              label="Presencial"
            />
            <FilterSwitch
              enabled={localFilters.remoto}
              onToggle={toggleRemoto}
              label="Remoto"
            />
          </div>
        </div>

        {/* Full-width divider between groups */}
        <div
          style={{
            height: '1px',
            backgroundColor: 'var(--border)',
            marginTop: '24px',
            marginBottom: '0px',
          }}
        />

        {/* GROUP D — POR AVALIAÇÃO */}
        {/*<div
          style={{
            paddingTop: '24px',
            paddingBottom: '24px',
            paddingLeft: '20px',
            paddingRight: '20px',
          }}
        >
          <h4
            style={{
              fontSize: 'var(--text-xs)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--muted-foreground)',
              textTransform: 'uppercase',
              letterSpacing: '0.025em',
              marginBottom: '12px',
            }}
          >
            Avaliação
          </h4>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0px',
              backgroundColor: 'var(--muted)',
              borderRadius: 'var(--radius-xl)',
              padding: '8px 20px',
            }}
          >
            <FilterSwitch
              enabled={localFilters.gostei}
              onToggle={toggleGostei}
              label="Gostei"
            />
            <FilterSwitch
              enabled={localFilters.naoGostei}
              onToggle={toggleNaoGostei}
              label="Não gostei"
            />
            <FilterSwitch
              enabled={localFilters.naoAvaliados}
              onToggle={toggleNaoAvaliados}
              label="Sem avaliação"
            />
          </div>
        </div>*/}
      </div>
    </BaseActionSheet>
  );
}
